import { useField } from 'formik';
import { twMerge } from 'tailwind-merge';
import { useMemo, ReactNode } from 'react';
import {
  Radio,
  IconButton,
  RadioGroup,
  Typography,
  FormControl,
  FormHelperText,
} from '@mui/material';

import { useIsMobile } from 'hooks';
import { controlError } from 'utils';

import { Icon, RadioCheckedIcon } from '../icons';

export interface ScaleRatingFieldProps {
  min: number;
  max: number;
  name: string;
  label?: string;
  header?: ReactNode;
  labelClassName?: string;
  stretchLayout?: boolean;
}

export const ScaleRatingField = ({
  min,
  max,
  name,
  label,
  header,
  labelClassName,
  stretchLayout = false,
}: ScaleRatingFieldProps) => {
  const [field, meta, helpers] = useField<number | null>(name);
  const isMobile = useIsMobile();

  const errorText = controlError(meta, name, label);
  const hasError = Boolean(errorText);

  const { width, items } = useMemo(() => {
    const scaleItems = [...Array(max - min + 1)].map((_, i) => min + i);
    const size = isMobile ? 28 : 32;
    const spacing = isMobile ? 8 : 10;
    const scaleWidth =
      scaleItems.length * size + (scaleItems.length - 1) * spacing;
    return { width: scaleWidth, items: scaleItems };
  }, [min, max, isMobile]);

  return (
    <FormControl error={hasError}>
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        {label && (
          <Typography
            className={twMerge(
              labelClassName,
              stretchLayout && 'md:w-[40%] md:min-w-[40%]'
            )}
          >
            {label}
          </Typography>
        )}

        <div
          style={stretchLayout ? undefined : { width }}
          className={stretchLayout ? 'w-full' : undefined}
        >
          {header}
          {stretchLayout ? (
            <RadioGroup
              row
              name={`${name}-group`}
              className="flex w-full gap-2"
              value={field.value == null ? '' : String(field.value)}
              onChange={(_, selectedValue) =>
                helpers.setValue(Number(selectedValue), true)
              }
            >
              {items.map((value) => (
                <div key={value} className="flex flex-1">
                  <Radio
                    value={String(value)}
                    icon={<Icon.RadioEmpty />}
                    checkedIcon={<RadioCheckedIcon />}
                    slotProps={{
                      input: {
                        'aria-label': `${value} out of ${max}`,
                      },
                    }}
                  />
                </div>
              ))}
            </RadioGroup>
          ) : (
            <div className="flex gap-2 md:gap-2.5">
              {items.map((value) => {
                const isSelected = field.value === value;

                return (
                  <IconButton
                    key={value}
                    onClick={() => helpers.setValue(value, true)}
                    className={twMerge(
                      'h-7 w-7 md:h-8 md:w-8',
                      isSelected
                        ? 'bg-accent-gradient-primary-primary hover:bg-accent-gradient-primary-hover active:bg-accent-gradient-primary-active'
                        : 'border-gray-primary border-[1.5px]'
                    )}
                  >
                    <Typography
                      className={
                        isSelected ? 'text-white-primary' : 'text-black-primary'
                      }
                    >
                      {value}
                    </Typography>
                  </IconButton>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {hasError && <FormHelperText>{errorText}</FormHelperText>}
    </FormControl>
  );
};
